import type { HistoryMedia, HistoryMessage } from './conversationsApi'

/**
 * Fase 3 — adaptação do `/history` para o formato que a tela já renderiza.
 *
 * A Fase 2 mede se a API tem tudo o que o SDK tem. Esta camada é o que permite
 * **usar** essa API como fonte de leitura, mantendo o SDK para escrita, tempo
 * real e typing.
 *
 * Nada aqui faz requisição: é conversão pura, para poder ser testada sem rede e
 * sem SDK.
 */

/** Mensagem no formato da tela. Espelha `ServiceRoomChatMessage` do hook. */
export interface AdaptedMessage {
  sid: string
  body: string
  author: string
  timestamp: Date
  twilioMessageIndex?: number
  attributes?: {
    fileUrl?: string
    fileName?: string
    fileSize?: number
    contentType?: string
    deleted?: boolean
    /** Anexo que existe mas cuja URL não pôde ser resolvida. */
    mediaError?: boolean
  }
}

/**
 * Prefixo do gateway.
 *
 * `media[].url` vem **relativa ao serviço**, não ao gateway: usar o valor cru
 * dá 404. Isso custou uma investigação e está registrado nas três specs.
 */
const GATEWAY_PREFIX = '/conversations'

export function resolveHistoryMediaUrl(
  url: string | undefined | null
): string | null {
  const raw = (url ?? '').trim()
  if (!raw) return null
  // Absoluta: o backend já resolveu, não mexer.
  if (/^https?:\/\//i.test(raw)) return raw
  const path = raw.startsWith('/') ? raw : `/${raw}`
  // Idempotente: se já vier com o prefixo, não duplicar.
  if (path.startsWith(`${GATEWAY_PREFIX}/`)) return path
  return `${GATEWAY_PREFIX}${path}`
}

function firstMedia(m: HistoryMessage): HistoryMedia | null {
  return Array.isArray(m.media) && m.media.length > 0 ? m.media[0] : null
}

export function historyMessageHasContent(m: HistoryMessage): boolean {
  const text = m.body != null ? String(m.body).trim() : ''
  if (text.length > 0) return true
  return firstMedia(m) != null
}

/**
 * Autoria.
 *
 * `author.role` é o campo declarado para isso e não depende de convenção de
 * string. A `identity` continua servindo de fallback — é a identidade
 * namespaced do Twilio, a mesma que o `POST /token` devolve.
 */
export function historyAuthorLabel(m: HistoryMessage): string {
  return m.author?.identity ?? m.author?.role ?? ''
}

/**
 * Converte um item do `/history` no formato da tela.
 *
 * A legenda de anexo (`📎 nome`) segue a mesma regra do mapeamento do SDK, para
 * que uma mensagem não mude de aparência conforme a fonte que a trouxe.
 */
export function mapHistoryMessage(m: HistoryMessage): AdaptedMessage {
  const media = firstMedia(m)
  const body = m.body != null ? String(m.body) : ''
  const timestamp = m.createdAt ? new Date(m.createdAt) : new Date(0)

  const base: AdaptedMessage = {
    sid: m.messageSid,
    body,
    author: historyAuthorLabel(m),
    timestamp,
    ...(typeof m.index === 'number' && !Number.isNaN(m.index)
      ? { twilioMessageIndex: m.index }
      : {}),
  }

  if (!media) return base

  const url = resolveHistoryMediaUrl(media.url)
  const name = media.filename || 'Anexo'

  return {
    ...base,
    body: body.trim() ? body : `📎 ${name}`,
    attributes: {
      fileName: media.filename,
      fileSize: media.sizeBytes,
      contentType: media.contentType,
      ...(url ? { fileUrl: url } : { mediaError: true }),
    },
  }
}

/**
 * Ordena por `(createdAt, messageSid)` — a mesma ordem do backend.
 *
 * O índice do Twilio não serve: reinicia por conversa. O timestamp sozinho
 * também não: repete dentro do mesmo segundo, e aí a ordem ficaria instável
 * entre renderizações. O sid desempata de forma determinística.
 */
export function sortAdaptedMessages(list: AdaptedMessage[]): AdaptedMessage[] {
  return [...list].sort((a, b) => {
    const ta = a.timestamp?.getTime?.() ?? 0
    const tb = b.timestamp?.getTime?.() ?? 0
    if (ta !== tb) return ta - tb
    return a.sid.localeCompare(b.sid)
  })
}

/**
 * Junta as duas fontes deduplicando **exclusivamente por `messageSid`**.
 *
 * Índice reinicia por conversa e timestamp repete no mesmo segundo — nenhum dos
 * dois identifica mensagem entre fontes diferentes.
 *
 * Em caso de colisão vence a versão que **já está na tela**: ela pode ter uma
 * URL de mídia recém-resolvida pelo SDK que o `/history` não traz.
 */
export function mergeBySid(
  atuais: AdaptedMessage[],
  novas: AdaptedMessage[]
): AdaptedMessage[] {
  const porSid = new Map<string, AdaptedMessage>()
  novas.forEach((m) => porSid.set(m.sid, m))
  atuais.forEach((m) => porSid.set(m.sid, m))
  return sortAdaptedMessages(Array.from(porSid.values()))
}
